import { z } from 'zod';
import { MONEY_CENTS_MAX } from '../limits.js';
/**
 * ID format per data model §2: `<PREFIX>-<unix-ms>-<rand>` where PREFIX is 3-4 uppercase
 * letters and rand is 4-6 lowercase alphanumeric chars. Account uses 4-char rand;
 * AuditEntry uses 6-char rand.
 */
export const IDSchema = z.string().regex(/^[A-Z]{3,4}-\d+-[a-z0-9]{4,6}$/);
export const IsoTimestampSchema = z.string().datetime();
export const EmailSchema = z.string().email();
/**
 * Quote id format (RIG-293). Quotes use a datestamp id `RR-<YYYYMMDD>-<rand>`
 * (see `generateQuoteId`), NOT the generic `IDSchema` entity-id shape (which
 * requires a 3-4 letter prefix — `RR` is only 2). This dedicated regex pins
 * the real format on QuoteSchema.id and on the *quoteId foreign keys that
 * reference it, mirroring the `INV-` precedent in invoice.ts. Accepts a 4-6
 * char rand suffix to stay forward-compatible with higher-volume id gens.
 */
export const QuoteIdSchema = z.string().regex(/^RR-\d{8}-[a-z0-9]{4,6}$/);
/**
 * RIG-657 — contact id format. Contacts use `generateId('CONTACT', 4)`, so
 * their ids are `CONTACT-<unix-ms>-<rand>`: a 7-letter prefix the generic
 * `IDSchema` (3-4 letters) rejects. Every field that carries a contact id uses
 * this schema; validating one with `IDSchema` refused every real contact
 * ("Send accept link" to a named contact returned 400 for all of them).
 */
export const ContactIdSchema = z.string().regex(/^CONTACT-\d+-[a-z0-9]{4,6}$/);
/** Money in integer cents to avoid floating-point drift. */
// QA 2026-10-01 F-08 — money is integer cents in int4 columns; the ceiling
// ($20,000,000.00) keeps every amount below the int4 maximum.
export const MoneyCentsSchema = z.number().int().nonnegative().max(MONEY_CENTS_MAX);
/**
 * A whole-number discount/markup percentage, 0-100 (RIG-295). Pins the
 * business invariant in the schema so a builder/UI regression can't push a
 * nonsensical 200%+ value through validation into the pricing math.
 */
export const DiscountPctSchema = z.number().int().nonnegative().max(100);
/**
 * MSA template version (RIG-328). A positive integer, shared by Deal and
 * Submission so the same logical "MSA template version stamped at
 * proposal-send time" has ONE representation across the catalog (previously
 * Deal stored it as text and Submission as a number, forcing defensive casts).
 */
export const MsaTemplateVersionSchema = z.number().int().positive();
export const SchemaVersionSchema = z.number().int().positive().default(1);
/**
 * Per-record `history[]` entry. Capped at last 100 entries (FIFO drop on append).
 * Stored as JSONB. Canonical source is the `audit_log` table; this is a UI-fast projection.
 */
export const HistoryEntrySchema = z.object({
    eventType: z.string(),
    at: IsoTimestampSchema,
    by: z.string(),
    note: z.string().optional(),
    payload: z.record(z.unknown()).optional(),
});
//# sourceMappingURL=shared.js.map